import {
    useCallback, useMemo,
} from 'react'
import {
    renderToString,
} from 'react-dom/server'
import ReactApexChart from 'react-apexcharts'
import {
    useTheme,
} from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import useContainerManagementRoutes from 'app/SkyNetRoutes/hooks/useContainerManagementRoutes'
import Card from 'app/shared-components/Card'
import StatusHandler from 'app/shared-components/StatusHandler'
import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'

import useGetTruckCapacity from '../../hooks/useGetTruckCapacity'
import CustomTooltip from './CustomTooltip'
import getOptions from './optionsChart'
import useStyles from './TruckCapacity.style'

const TruckCapacity = ({
    id,
}: {
    id: number,
}) => {
    const theme = useTheme()
    const {
        classes,
    } = useStyles()
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetTruckCapacity({
        serviceCenter: {
            id,
        },
    })

    const {
        ServiceCenters: ServiceCentersRoutes,
    } = useContainerManagementRoutes()

    const link = generateSkyNetLink({
        domainPath: ServiceCentersRoutes.RelatedOrders.path,
        params: {
            key: id,
        },
    })

    const chartData: {
        series: { name: string, data: number[] }[],
        categories: string[],
    } = useMemo(() => {
        const transformedData = data?.trucksAmount?.reduce((acc, {
            inboundOrdersTrucksAmount,
            outboundOrdersTrucksAmount,
            date,
        }) => {
            return {
                inbound: {
                    ...acc.inbound,
                    data: [
                        ...acc.inbound.data,
                        inboundOrdersTrucksAmount,
                    ],
                },
                outbound: {
                    ...acc.outbound,
                    data: [
                        ...acc.outbound.data,
                        outboundOrdersTrucksAmount,
                    ],
                },
                categories: [
                    ...acc.categories,
                    date,
                ],
            }
        }, {
            inbound: {
                name: 'Inbound',
                data: [],
            },
            outbound: {
                name: 'Outbound',
                data: [],
            },
            categories: [],
        })

        return {
            series: [
                transformedData?.inbound,
                transformedData?.outbound,
            ],
            categories: transformedData?.categories,
        }
    }, [data])

    const customTooltip = useCallback(({
        series: selectedSeries,
        dataPointIndex,
        w,
    }: {
        series: number[],
        dataPointIndex: number,
        w: {
            globals: {
                labels: string[],
            }
        }
    }) => {
        return renderToString(
            <CustomTooltip
                date={w.globals.labels[dataPointIndex]}
                maxTrucksPerDay={data?.maxTrucksPerDay}
                inboundOrdersTrucksAmount={selectedSeries[0][dataPointIndex]}
                outboundOrdersTrucksAmount={selectedSeries[1][dataPointIndex]}
                inboundOrders={data?.trucksAmount?.[dataPointIndex].inboundOrdersAmount}
                outboundOrders={data?.trucksAmount?.[dataPointIndex].outboundOrdersAmount}
                classNames={{
                    inbound: classes.inbound,
                    outbound: classes.outbound,
                }}
            />,
        )
    }, [
        classes.inbound,
        classes.outbound,
        data?.maxTrucksPerDay,
        data?.trucksAmount,
    ])

    const options = useMemo(() => {
        return getOptions({
            categories: chartData?.categories,
            customTooltip,
            maxTrucksPerDay: data?.maxTrucksPerDay,
            theme,
        })
    }, [
        chartData?.categories,
        customTooltip,
        data?.maxTrucksPerDay,
        theme,
    ])

    return (
        <Card
            contentClass={classes.cardContent}
            data-testid="truck-capacity-card"
        >
            <div className={classes.headerWrapper}>
                <Typography variant="h3">
                    Truck Capacity Usage Ratio
                </Typography>
                <SkyNetLink
                    type={TypeLink.EXTERNAL}
                    size={SizeLink.M}
                    link={link}
                    text="See Related Orders"
                    customClasses={{
                        wrapper: classes.linkWrapper,
                    }}
                />
            </div>
            <StatusHandler
                isSuccess={isDataReady}
                isFetching={isFetching}
                isError={isError}
                error={error}
            >
                {data && (
                    <ReactApexChart
                        options={options}
                        height={700}
                        style={{
                            width: '100%',
                        }}
                        series={chartData.series}
                        type="bar"
                    />
                )}
            </StatusHandler>
        </Card>
    )
}

export default TruckCapacity
